import * as Effect from "effect/Effect";
import { create } from "zustand";

import { clearKey, getStoredKey, storeKey, type HeldKey } from "../../shared/auth/key-store";
import { platformApiBaseUrl } from "../../shared/auth/platform-api-origin";
import { PrimaryEnvironmentHttpClient } from "./httpClient";
import { runPrimaryHttp } from "../../lib/runtime";
import { currentServerToken, serverTokenSubject } from "./serverToken";

/**
 * Gets the signed-in user's `dvk_` inference credential into their server: the
 * desktop's local server, or their team's server, alike.
 *
 * The user never sees or handles a key. The window is the only party that can
 * mint one — `POST /api/me/keys` is session-cookie gated and the server holds no
 * session — so it mints, remembers, and pushes the credential inward over the
 * server connection it has already authenticated. The server holds it for the
 * user the connection's token names.
 *
 * Bootstrap and a turn without a held key share one acquisition per user. The
 * server keeps the credential in memory, so bootstrap re-pushes a cached key
 * after a restart. A turn retries an unsuccessful acquisition because the
 * organisation's subscription can become active while the page stays open.
 */

/** What the caller learns. Never carries key material. */
export type InferenceCredentialOutcome =
  /** The server holds a credential. */
  | { readonly status: "held" }
  /**
   * platform-api refused to issue one because the account has no active
   * entitlement. Signed in but not entitled is a real product state, and it must
   * report differently from a broken credential path.
   */
  | { readonly status: "not-entitled" }
  /** Something failed. Sign-in is not blocked; inference will report it. */
  | { readonly status: "unavailable"; readonly reason: string };

/** The last acquisition result distinguishes access refusals from credential outages. */
export type InferenceCredentialStatus = InferenceCredentialOutcome["status"] | "pending";

export const useInferenceCredentialStatus = create<{
  readonly status: InferenceCredentialStatus;
  readonly userId: string | null;
}>(() => ({ status: "pending", userId: null }));

const acquisitions = new Map<string, Promise<InferenceCredentialOutcome>>();

/** The one response that ever carries key material, from `POST /api/me/keys`. */
interface IssuedKey {
  readonly id: string;
  readonly key: string;
}

/** What minting produced, or the status that explains why it did not. */
type IssueOutcome =
  | { readonly ok: true; readonly id: string; readonly key: string }
  | { readonly ok: false; readonly status: number };

async function issueKey(): Promise<IssueOutcome> {
  const response = await fetch(`${platformApiBaseUrl}/api/me/keys`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ label: "dovod-server" }),
  });

  if (!response.ok) {
    return { ok: false, status: response.status };
  }

  const body = (await response.json()) as IssuedKey;
  return { ok: true, id: body.id, key: body.key };
}

/**
 * Never rejects. It is called from the auth bootstrap without being awaited into
 * the gate's result, so a rejection here would be an unhandled promise:
 * `getStoredKey()` touches `window.localStorage`, and the bootstrap's own unit
 * tests run with no `window` at all.
 */
export function ensureInferenceCredential(userId: string): Promise<InferenceCredentialOutcome> {
  const pending = acquisitions.get(userId);
  if (pending !== undefined) return pending;

  useInferenceCredentialStatus.setState({ status: "pending", userId });
  const attempt = acquire(userId).finally(() => acquisitions.delete(userId));
  acquisitions.set(userId, attempt);
  return attempt;
}

async function acquire(userId: string): Promise<InferenceCredentialOutcome> {
  let outcome: InferenceCredentialOutcome;
  try {
    outcome = await ensure(userId);
  } catch (error) {
    outcome = { status: "unavailable", reason: describe(error) };
  }
  useInferenceCredentialStatus.setState({ status: outcome.status, userId });
  return outcome;
}

/** A turn waits for its user's key to reach the server before consuming the draft. */
export function ensureInferenceCredentialForTurn(): Promise<InferenceCredentialOutcome> {
  const token = currentServerToken();
  const userId = token === null ? null : serverTokenSubject(token);
  if (userId === null) {
    useInferenceCredentialStatus.setState({ status: "unavailable", userId: null });
    return Promise.resolve({ status: "unavailable", reason: "session_missing" });
  }
  const pending = acquisitions.get(userId);
  if (pending !== undefined) return pending;
  const state = useInferenceCredentialStatus.getState();
  return state.status === "held" && state.userId === userId
    ? Promise.resolve({ status: "held" })
    : ensureInferenceCredential(userId);
}

/**
 * Takes the user's credential back out of the server.
 *
 * `POST /api/inference/credential` can only carry a non-empty key, so this is the
 * only way for the window to leave the server holding nothing for its user. It
 * matters at every point where the window stops vouching for a key: sign-out, a
 * cached key that turned out to be revoked, and an account transition whose own
 * key could not be issued. Without it the server keeps the key and bills the
 * user's next turn against it.
 *
 * Never rejects, for the same reason `ensureInferenceCredential` does not: the
 * bootstrap and the sign-out path both call it without a place to put an error.
 */
export async function clearInferenceCredential(): Promise<void> {
  useInferenceCredentialStatus.setState({ status: "pending", userId: null });
  try {
    await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) => client.inference.clearCredential({ headers: {} })),
      ),
    );
  } catch (error) {
    console.warn("[dovod] could not drop the server's inference credential", describe(error));
  }
}

/**
 * Revokes the held key at platform-api (`POST /api/me/keys/:id/revoke`), so the
 * key ends with the sign-in it was minted for wherever a copy of it survives.
 *
 * Runs while the session cookie still exists: the route is the session's own.
 * Never rejects, and a failed revoke is only logged, because sign-out must
 * complete either way; the key then lives until the operator or its expiry
 * ends it.
 */
export async function revokeInferenceKey(): Promise<void> {
  const held = getStoredKey();
  if (held === null) return;
  try {
    const response = await fetch(
      `${platformApiBaseUrl}/api/me/keys/${encodeURIComponent(held.id)}/revoke`,
      { method: "POST", credentials: "include" },
    );
    if (!response.ok) {
      console.warn("[dovod] could not revoke the inference credential", response.status);
    }
  } catch (error) {
    console.warn("[dovod] could not revoke the inference credential", describe(error));
  }
}

const describe = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/**
 * Whether the cached credential is still one `userId` may present.
 *
 * A key minted for another account sharing this browser is never presented:
 * the server would bill this user's turns to it. That is decided before asking
 * platform-api anything, so no answer of the check — conclusive or not — can
 * keep it.
 *
 * `GET /api/me/keys` returns metadata only, so the stored id — not the key — is
 * what makes liveness answerable at all.
 *
 * A failure of the *check* is not a failure of the user's own key: a transient
 * platform-api outage must not force a re-mint, because that leaves a live key
 * behind on every hiccup and grows the table without bound. So an unreachable
 * check keeps the user's cached key; if it really is revoked, inference reports
 * it as it did before.
 */
async function isStillLive(held: HeldKey, userId: string): Promise<boolean> {
  if (held.userId !== userId) {
    return false;
  }
  const response = await fetch(`${platformApiBaseUrl}/api/me/keys`, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    return true;
  }
  const body: unknown = await response.json();
  if (typeof body !== "object" || body === null || !("keys" in body) || !Array.isArray(body.keys)) {
    return true;
  }
  const now = Date.now();
  for (const entry of body.keys) {
    if (typeof entry !== "object" || entry === null || !("id" in entry)) continue;
    if (entry.id !== held.id) continue;
    const revoked = "revokedAt" in entry && entry.revokedAt !== null;
    const expiry =
      "expiresAt" in entry && typeof entry.expiresAt === "string"
        ? Date.parse(entry.expiresAt)
        : null;
    return !revoked && (expiry === null || Number.isNaN(expiry) || expiry > now);
  }
  // Present in storage but absent from the account's keys: a different
  // deployment's key, or a deleted row. Either way it cannot be presented.
  return false;
}

async function ensure(userId: string): Promise<InferenceCredentialOutcome> {
  const held = getStoredKey();
  let credential: string | null = null;

  if (held !== null && (await isStillLive(held, userId))) {
    credential = held.key;
  } else {
    // Also drops a value that does not parse as a key record at all.
    clearKey();
  }

  if (credential === null) {
    let issued: IssueOutcome;
    try {
      issued = await issueKey();
    } catch (error) {
      return await nothingToPresent({ status: "unavailable", reason: describe(error) });
    }

    if (!issued.ok) {
      // 403 is the entitlement gate, which is a product state rather than a
      // fault. Everything else is a fault the caller should be able to name.
      return await nothingToPresent(
        issued.status === 403
          ? { status: "not-entitled" }
          : { status: "unavailable", reason: `issue responded ${issued.status}` },
      );
    }

    storeKey({ id: issued.id, key: issued.key, userId });
    credential = issued.key;
  }

  try {
    const result = await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) =>
          client.inference.setCredential({ headers: {}, payload: { credential } }),
        ),
      ),
    );
    return result.held ? { status: "held" } : { status: "unavailable", reason: "server_rejected" };
  } catch (error) {
    return { status: "unavailable", reason: describe(error) };
  }
}

/**
 * The window has no key it is willing to present, so the server must not be
 * left holding the last one it was given for
 * this user: a key whose issuance is now refused or unavailable would keep
 * running, and billing, the user's turns.
 */
async function nothingToPresent(
  outcome: InferenceCredentialOutcome,
): Promise<InferenceCredentialOutcome> {
  await clearInferenceCredential();
  return outcome;
}
